"use client";

import { useEffect, useRef, useState } from "react";
import { useOspApiClientProvider } from "@hooks/useOspApiClientProvider";
import { Track } from "@/src/lib/tracking";
import type { FinancialSyncStatusResponse } from "@features/api/FinancialSyncStatusApi";
import { parseFinancialSyncStatus } from "./parseFinancialSyncStatus";
import {
  FINANCIAL_SYNC_WAIT_BUDGET_MS,
  isSyncStatusDone,
  resolveSyncGateView,
} from "./syncStatusView";

export {
  isSyncStatusDone,
  resolveSyncProgressView,
  resolveSyncGateView,
  syncAccountTypesKnown,
  isInvestAccountType,
} from "./syncStatusView";
export type { SyncProgressView, SyncGateView } from "./syncStatusView";

export interface UseFinancialSyncStatusOptions {
  /** Poll only while true (e.g. component mounted + waiting state). */
  enabled: boolean;
  /** Poll interval. Contract default 2s. */
  intervalMs?: number;
  /** Hard stop for the polling loop; the last known status is kept. */
  timeoutMs?: number;
  /**
   * 只有银行账户（没有投资账户）时 `isSyncStatusDone` 也会成立而停止轮询；设为 true 则这种状态下继续轮询，
   * 等投资账户的索引条目出现（SyncHandoff 重叠分析用，review #123）。
   */
  holdOnNoInvest?: boolean;
}

export interface UseFinancialSyncStatusResult {
  /** null = API absent/failed/undeployed — consumers must render current copy. */
  status: FinancialSyncStatusResponse | null;
  /** true only while ticks are actively scheduled. */
  polling: boolean;
  /** Completion contract: summary COMPLETE with a non-empty fully covered set. */
  done: boolean;
}

/**
 * Completion contract — see ./syncStatusView.ts (shared truth table).
 */

const DEFAULT_INTERVAL_MS = 2_000;
const DEFAULT_TIMEOUT_MS = FINANCIAL_SYNC_WAIT_BUDGET_MS;

/**
 * Poll GET /v2/financial/sync-status while enabled.
 *
 * Failure contract: any error (undeployed route, network, auth) degrades to
 * `status: null` silently — polling continues until enabled/timeout/settled
 * because the backend may come up mid-wait; the UI never surfaces the error.
 * Stops early only on the completion contract (summary COMPLETE over a fully
 * covered non-empty total). IDLE and COMPLETE-with-gap keep polling — events
 * for older/missed accounts may still land (backend aggregates the account
 * directory as the total, so the gap closes server-side).
 */
export function useFinancialSyncStatus({
  enabled,
  intervalMs = DEFAULT_INTERVAL_MS,
  timeoutMs = DEFAULT_TIMEOUT_MS,
  holdOnNoInvest = false,
}: UseFinancialSyncStatusOptions): UseFinancialSyncStatusResult {
  const { financialSyncStatusApi } = useOspApiClientProvider();
  const [status, setStatus] = useState<FinancialSyncStatusResponse | null>(
    null,
  );
  const [stopped, setStopped] = useState(true);
  const startedAtRef = useRef<number | null>(null);
  const timerRef = useRef<number | null>(null);
  const inFlightRef = useRef(false);
  const cancelTokenRef = useRef<symbol | null>(null);

  useEffect(() => {
    if (!enabled || !financialSyncStatusApi) {
      setStopped(true);
      return;
    }
    setStopped(false);
    startedAtRef.current = Date.now();

    const tick = async () => {
      const startedAt = startedAtRef.current;
      if (startedAt !== null && Date.now() - startedAt >= timeoutMs) {
        // Timeout: keep the last known status, stop polling.
        if (timerRef.current !== null) {
          window.clearInterval(timerRef.current);
          timerRef.current = null;
        }
        setStopped(true);
        return;
      }
      // EDG-529 轮询健壮性（裁定 2026-09-10）：串行轮询——上一笔未完（含服务端
      // hang 住）绝不发下一笔，防止 setInterval 在后端卡死时无限堆 pending。
      if (inFlightRef.current) {
        return;
      }
      inFlightRef.current = true;
      let requestTimer: number | null = null;
      const cancelToken = Symbol("financial-sync-status");
      cancelTokenRef.current = cancelToken;
      try {
        // EDG-529：请求级超时——后端 hang 住（如远程依赖超时重试）时 10s
        // 放弃本笔，走静默降级路径，下一轮 interval 再试；绝不长期挂起。
        const REQUEST_TIMEOUT_MS = 10_000;
        const res = (await Promise.race([
          financialSyncStatusApi.financialSyncStatus.getSyncStatus({
            cancelToken,
          }),
          new Promise<never>((_, reject) => {
            requestTimer = window.setTimeout(() => {
              // Promise.race alone only stops waiting; explicitly abort the
              // underlying fetch so the next tick cannot overlap a hung one.
              financialSyncStatusApi.abortRequest?.(cancelToken);
              reject(new Error("sync-status request timeout"));
            }, REQUEST_TIMEOUT_MS);
          }),
        ])) as {
          data?:
            | { data?: FinancialSyncStatusResponse }
            | FinancialSyncStatusResponse;
        };
        const next = parseFinancialSyncStatus(res);
        if (next && typeof next.summary === "string") {
          const normalized = {
            ...next,
            accounts: Array.isArray(next.accounts) ? next.accounts : [],
          };
          setStatus(normalized);
          if (
            isSyncStatusDone(normalized) &&
            !(
              holdOnNoInvest &&
              resolveSyncGateView(normalized).kind === "no-invest"
            )
          ) {
            // Completion contract satisfied: stop polling, keep status.
            if (timerRef.current !== null) {
              window.clearInterval(timerRef.current);
              timerRef.current = null;
            }
            setStopped(true);
          }
        }
      } catch (err) {
        // Silent degradation: absent route / network failure → keep null.
        Track.log("warn", "financialSyncStatus", "poll failed (silently)", {
          msg: (err as Error)?.message ?? "unknown",
        });
      } finally {
        if (requestTimer !== null) {
          window.clearTimeout(requestTimer);
        }
        if (cancelTokenRef.current === cancelToken) {
          cancelTokenRef.current = null;
        }
        inFlightRef.current = false;
      }
    };

    void tick();
    timerRef.current = window.setInterval(() => {
      void tick();
    }, intervalMs);

    return () => {
      startedAtRef.current = null;
      if (timerRef.current !== null) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
      if (cancelTokenRef.current !== null) {
        financialSyncStatusApi.abortRequest?.(cancelTokenRef.current);
        cancelTokenRef.current = null;
      }
    };
  }, [enabled, financialSyncStatusApi, holdOnNoInvest, intervalMs, timeoutMs]);

  const done =
    isSyncStatusDone(status) &&
    !(holdOnNoInvest && resolveSyncGateView(status).kind === "no-invest");

  return { status, polling: enabled && !done && !stopped, done };
}
